import { ChangeEvent, KeyboardEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { emailValidationSchema, ValidationErrors } from 'src/constants/validations';
import { useAuthenticationApi } from '../api';

export const useForgotPasswordViewController = () => {
  const api = useAuthenticationApi();
  const navigate = useNavigate();

  const [emailSent, setEmailSent] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [formValues, setFormValues] = useState({ email: '' });

  const getSupportEmail = () => {
    const hostname = window.location.hostname;
    const domain = hostname.replace(/^(app-dev\.|app-qa\.|app-local\.|app\.)/, '');
    return `support@${domain}`;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const validate = async (values: typeof formValues) => {
    try {
      await emailValidationSchema.validate(values, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors = err.inner?.reduce((acc, curr) => {
          if (curr.path) {
            acc[curr.path] = curr?.message;
          }
          return acc;
        }, {} as ValidationErrors);
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSendEmail = async () => {
    const isValid = await validate(formValues);
    if (isValid) {
      api
        .forgotPassword(formValues)
        .then(() => {
          setEmailSent(true);
        })
        .catch(e => {
          if (e.error === 'not_exists') {
            setEmailSent(true);
          }
        });
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement | HTMLTextAreaElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSendEmail();
    }
  };

  const navigateToLogin = () => {
    navigate('/login');
  };

  return {
    emailSent,
    errors,
    formValues,
    getSupportEmail,
    handleChange,
    handleSendEmail,
    onKeyDown,
    navigateToLogin,
  };
};
